import type { AppLocale, MessageTree } from "@airange/shared/i18n-common";

export const redblueMemberAuthPageMessages: Record<AppLocale, MessageTree> = {
  "zh-CN": {
    redblue: {
      pages: {
        members: {
          subtitle:
            "维护 redblue 本地应用成员清单，并为成员分配应用级角色；前后台准入与活动内选人都以这里为准。",
          fields: {
            username: "用户名",
            email: "邮箱",
            appRole: "应用级角色",
          },
          actions: {
            confirmAddMember: "加入成员",
            removeMember: "移除成员",
          },
          preview: {
            selectedMembers: "已选成员",
            emptySelectedMembers: "尚未选择成员",
          },
          picker: {
            title: "从统一身份中心添加成员",
            submitText: "带回已选成员",
            treeTitle: "组织树",
            tableTitlePrefix: "待加入成员",
          },
          dialogs: {
            removeTitle: "确认移除 {member} 吗？",
            removeContent: "移除后该成员将不能进入 redblue 前台和后台，也不能再被活动成员选择。",
            removeConfirm: "确认移除",
            editRolesTitleWithMember: "调整应用角色 · {member}",
          },
          messages: {
            loadDirectoryFailed: "加载成员目录失败",
            noAppRoleGranted: "未授予应用级角色",
            governanceNotice:
              "这里是 redblue 的应用级准入清单。前台、后台和活动内成员选人都必须以这里的本地成员为准。",
            memberRemoved: "成员已移除",
            membersAdded: "已新增 {count} 名成员",
            addMemberHint: "先从统一身份组织树中多选成员，回到当前抽屉预览后再确认加入。",
            memberRolesUpdated: "成员角色已更新",
            roleCatalogLoading: "正在加载应用角色目录，请稍后再调整角色。",
            roleCatalogUnavailable:
              "应用角色目录不可用，无法调整或展示角色，请检查后端角色目录合同。",
            noRoleSelectedHint:
              "不勾选任何应用级角色时，该成员只保留本地成员记录，不能进入 redblue 前台或 redblue-admin 后台。",
          },
        },
        login: {
          summary:
            "使用 AIRange 单点登录进入红蓝对抗后台，后台准入以 redblue 本地成员与应用角色为准。",
          bootstrapTitle: "正在检测登录状态",
          bootstrapDescription: "系统正在同步当前 SSO 会话并核验 redblue 后台准入。",
          noSessionTitle: "尚未登录 AIRange",
          noSessionDescription: "请先完成单点登录，再进入 redblue 后台。",
          noAccessTitle: "当前账号没有 redblue 后台权限",
          noAccessDescription:
            "你的 SSO 会话已生效，但当前账号不在 redblue 后台本地成员清单或缺少后台角色。",
          enterAction: "进入后台",
          switchIdentityAction: "切换账号",
          redetectAction: "重新检测",
          authFailedTitle: "登录没有完成",
          authFailedDescription: "统一登录未能进入红蓝对抗后台，请保留下方错误信息并重新登录。",
          redirectDidNotStart:
            "统一登录跳转没有启动，请检查浏览器是否拦截跳转或服务是否返回了无效授权地址。",
          retryLoginAction: "重新登录",
        },
        oidcCallback: {
          completing: "正在完成 OIDC 登录...",
          retryingLogin: "正在重新进入统一登录...",
          missingCodeOrState: "OIDC 回调缺少 code/state 参数",
          bootstrapSessionFailed: "红蓝对抗后台本地会话初始化失败",
          callbackFailed: "OIDC 回调失败",
          authFailedTitle: "登录回调没有完成",
          redirectDidNotStart:
            "统一登录跳转没有启动，请检查浏览器是否拦截跳转或服务是否返回了无效授权地址。",
          retryLoginAction: "重新登录",
        },
      },
    },
  },
  en: {
    redblue: {
      pages: {
        members: {
          subtitle:
            "Maintain the local RedBlue member allowlist and application roles for both admin and portal access.",
          fields: {
            username: "Username",
            email: "Email",
            appRole: "App-level role",
          },
          actions: {
            confirmAddMember: "Add to members",
            removeMember: "Remove member",
          },
          preview: {
            selectedMembers: "Selected members",
            emptySelectedMembers: "No members selected",
          },
          picker: {
            title: "Add members from identity center",
            submitText: "Return selected members",
            treeTitle: "Organization tree",
            tableTitlePrefix: "Members to add",
          },
          dialogs: {
            removeTitle: "Remove {member}?",
            removeContent:
              "After removal, this member can no longer access the redblue portal or admin, and can no longer be selected as an activity member.",
            removeConfirm: "Confirm removal",
            editRolesTitleWithMember: "Edit app roles · {member}",
          },
          messages: {
            loadDirectoryFailed: "Failed to load member directory",
            noAppRoleGranted: "No app-level role assigned",
            governanceNotice:
              "This is the redblue app-level access roster. Portal access, admin access, and activity member selection must all use this local member registry.",
            memberRemoved: "Member removed",
            membersAdded: "{count} members added",
            addMemberHint:
              "Select multiple members from the shared identity organization tree, preview them in this drawer, and then confirm the add.",
            memberRolesUpdated: "Member roles updated",
            roleCatalogLoading: "Loading the app role catalog. Try editing roles again shortly.",
            roleCatalogUnavailable:
              "The app role catalog is unavailable, so roles cannot be edited or displayed. Check the backend role catalog contract.",
            noRoleSelectedHint:
              "If no app-level role is selected, this person remains only as a local member record and cannot enter the redblue portal or redblue-admin.",
          },
        },
        login: {
          summary:
            "Use AIRange SSO to enter the RedBlue admin. Access is enforced by the local RedBlue member list and application roles.",
          bootstrapTitle: "Checking sign-in status",
          bootstrapDescription:
            "Synchronizing the current SSO session and verifying RedBlue admin access.",
          noSessionTitle: "Not signed in to AIRange",
          noSessionDescription: "Complete SSO sign-in before entering the RedBlue admin.",
          noAccessTitle: "This account has no RedBlue admin access",
          noAccessDescription:
            "Your SSO session is valid, but the account is not in the local RedBlue member list or lacks admin roles.",
          enterAction: "Enter Admin",
          switchIdentityAction: "Switch Account",
          redetectAction: "Check Again",
          authFailedTitle: "Sign-in did not complete",
          authFailedDescription:
            "Unified sign-in could not enter the RedBlue admin. Keep the error below and sign in again.",
          redirectDidNotStart:
            "The unified sign-in redirect did not start. Check whether the browser blocked navigation or the service returned an invalid authorization URL.",
          retryLoginAction: "Sign in again",
        },
        oidcCallback: {
          completing: "Completing OIDC sign-in...",
          retryingLogin: "Starting unified sign-in again...",
          missingCodeOrState: "The OIDC callback is missing the code/state parameters",
          bootstrapSessionFailed: "Failed to initialize the local RedBlue admin session",
          callbackFailed: "OIDC callback failed",
          authFailedTitle: "Sign-in callback did not complete",
          redirectDidNotStart:
            "The unified sign-in redirect did not start. Check whether the browser blocked navigation or the service returned an invalid authorization URL.",
          retryLoginAction: "Sign in again",
        },
      },
    },
  },
  ja: {
    redblue: {
      pages: {
        members: {
          subtitle:
            "redblue のローカルメンバー一覧とアプリケーションロールを管理し、前台・后台の入場権限をここに収束します。",
          fields: {
            username: "ユーザー名",
            email: "メールアドレス",
            appRole: "アプリケーションロール",
          },
          actions: {
            confirmAddMember: "メンバーとして追加",
            removeMember: "メンバー削除",
          },
          preview: {
            selectedMembers: "選択済みメンバー",
            emptySelectedMembers: "メンバーが選択されていません",
          },
          picker: {
            title: "統合IDセンターからメンバーを追加",
            submitText: "選択したメンバーを反映",
            treeTitle: "組織ツリー",
            tableTitlePrefix: "追加予定メンバー",
          },
          dialogs: {
            removeTitle: "{member} を削除しますか？",
            removeContent:
              "削除後、このメンバーは redblue のポータルや管理画面に入れず、演習メンバーとしても選択できなくなります。",
            removeConfirm: "削除する",
            editRolesTitleWithMember: "アプリロール調整 · {member}",
          },
          messages: {
            loadDirectoryFailed: "メンバー一覧の読み込みに失敗しました",
            noAppRoleGranted: "アプリケーションロール未付与",
            governanceNotice:
              "ここは redblue のアプリレベル利用許可一覧です。ポータル、管理画面、演習内のメンバー選択はすべてこのローカルメンバー一覧を基準にします。",
            memberRemoved: "メンバーを削除しました",
            membersAdded: "{count} 名のメンバーを追加しました",
            addMemberHint:
              "統合IDの組織ツリーから複数メンバーを選び、このドロワーで確認してから追加してください。",
            memberRolesUpdated: "メンバーロールを更新しました",
            roleCatalogLoading:
              "アプリケーションロール一覧を読み込んでいます。少し待ってから再度調整してください。",
            roleCatalogUnavailable:
              "アプリケーションロール一覧を利用できないため、ロールの調整や表示ができません。バックエンドのロール一覧契約を確認してください。",
            noRoleSelectedHint:
              "アプリケーションロールを 1 つも付与しない場合、この人はローカルメンバー記録だけが残り、redblue ポータルや redblue-admin には入れません。",
          },
        },
        login: {
          summary:
            "AIRange SSO で RedBlue 管理画面へ入り、アクセス権は RedBlue のローカルメンバー一覧とアプリケーションロールで判定します。",
          bootstrapTitle: "ログイン状態を確認しています",
          bootstrapDescription:
            "現在の SSO セッションを同期し、RedBlue 管理画面へのアクセス権を検証しています。",
          noSessionTitle: "AIRange にまだログインしていません",
          noSessionDescription:
            "先に SSO ログインを完了してから RedBlue 管理画面へ進んでください。",
          noAccessTitle: "このアカウントには RedBlue 管理権限がありません",
          noAccessDescription:
            "SSO セッションは有効ですが、このアカウントは RedBlue のローカルメンバー一覧に存在しないか、管理ロールが不足しています。",
          enterAction: "管理画面へ入る",
          switchIdentityAction: "アカウントを切り替える",
          redetectAction: "再確認",
          authFailedTitle: "ログインが完了しませんでした",
          authFailedDescription:
            "統合ログインから RedBlue 管理画面へ入れませんでした。下記のエラーを保持して再ログインしてください。",
          redirectDidNotStart:
            "統合ログインへのリダイレクトが開始されませんでした。ブラウザが遷移をブロックしたか、サービスが無効な認可 URL を返していないか確認してください。",
          retryLoginAction: "再ログイン",
        },
        oidcCallback: {
          completing: "OIDC ログインを完了しています...",
          retryingLogin: "統合ログインを再開しています...",
          missingCodeOrState: "OIDC コールバックに code/state パラメータがありません",
          bootstrapSessionFailed: "RedBlue 管理画面のローカルセッション初期化に失敗しました",
          callbackFailed: "OIDC コールバックに失敗しました",
          authFailedTitle: "ログインコールバックが完了しませんでした",
          redirectDidNotStart:
            "統合ログインへのリダイレクトが開始されませんでした。ブラウザが遷移をブロックしたか、サービスが無効な認可 URL を返していないか確認してください。",
          retryLoginAction: "再ログイン",
        },
      },
    },
  },
};
